/*global require, define */
/*jshint devel:true*/
/**
 * @name ModuleManager
 * @class
 *
 * This script will detect frontend modules present on a web page and find the name of their Backbone controller class.
 * Then, it will call that controller class js file (which is supposed to take care of itself then).
 *
 * For ex: VoicemailSelector or ServiceSelectorController
 */
define(
	'ModuleManager',[
        'underscore',
		'jquery',
        'backbone'
	],
	function (_, $, Backbone) {
		'use strict';

		var ModuleCollection = Backbone.Collection.extend({
            className: 'ModuleCollection'
        });

		return {

            /**
             * Event aggregator object accessible for controllers to allow indirect communication among them via events.
             * @type {Backbone.Events}
             */
            eventBus: _.extend({}, Backbone.Events),

            /**
             * Collection of initialised controllers on the page.
             * @type {ModuleCollection}
             */
            controllerInstances: new ModuleCollection(),

			/**
			 * Name of the data attribute to use to find the controller's name.
			 * @type {String}
			 */
			moduleControllerDataAttribute: 'data-controller',

			/**
			* Class name applied to initialised controllers.
			* @type {String}
			*/
			initialisedControllerCssClass: 'moduleManager__controller_initialised',

			/**
			 * Retrieves a list of module containers on a specific DOM root.
			 * @param {DOMElement} [domRoot] DOM element in which module containers will be searched for
			 * @returns {jQuery} jQuery object containing the elements found
			 */
			getModuleContainers: function(domRoot) {
				return $('.module[' + this.moduleControllerDataAttribute + ']', domRoot || window.document);
			},

			/**
			 * Reads the JSON data associated to a module's HTML container.
			 * @warning It is important that the SCRIPT tag using this function is the previous sibling of the .module
			 * HTML container.
			 * @param {jQuery} $module The module element to read data from
			 * @return {Object} Object representing the module data
			 */
			getModuleData: function($module) {
				var data, $dataContainer, contents;

				if (!($module instanceof $)) {
					$module = $($module);
				}

				$dataContainer = $module.prev('script[type="text/moduleData"]');
				console.log('$dataContainer = ', $dataContainer);

				if ($dataContainer.length === 0) {
					return data;

					// Don't raise an error yet, we have to convert all the frontend modules to this before
					// throw new Error('ModuleManager.getModuleData(): Unable to find $dataContainer.');
				}

				contents = $dataContainer.html().replace(/\\\//g, '/');
				data = JSON.parse(contents);

				return data;
			},

			/**
			 * Searches for frontend module definitions in the local page and initialises them. Modules can be initalised only once.
			 * @param {Function} [callback] Callback function to run when all the modules are loaded. The function signature is
			 * callback(ModuleCollection controllerInstances)
			 * @returns {*}
			 */
			setupModules: function(callback) {
				var self = this,
					moduleContainers = this.getModuleContainers(),
					processedControllersCount = 0;

				var proxyCallback = function() {
					if ($.isFunction(callback)) {
						callback.call(self, self.controllerInstances);
					}
				};

				// when the module is initialised, increment counter and run the proxyCallback() if it's the last one
				var whenModuleIsProcessed = function() {
					var allProcessed = false;
					processedControllersCount++;

					// if we've processed all the controllers in the list
					if (processedControllersCount === moduleContainers.length) {
						proxyCallback();
						allProcessed = true;
					}

					return allProcessed;
				};

				if (moduleContainers.length === 0) {
					proxyCallback();
					return this;
				}

				// when the module is initialised (and added to the collection of controllers) notify interested controllers
                this.controllerInstances.on('add', function(module) {
                    self.triggerEvent(self.getModuleInitEventName(module.get('className')), module);
                });

				moduleContainers.each(function(index, module) {
					self.initialiseModule(module, whenModuleIsProcessed);
				});

				return this;
			},

			/**
			* Initialises a module by reading its data and instantiating its Backbone controller class.
			* @param {DOMElement} module DOM element representing the module to initialise
			* @param {Function} [processedCallback] Optional callback function to execute after the module has been initialised
			*/
			initialiseModule: function(module, processedCallback) {
				var $module = $(module),
					controllerClassName = $module.attr(this.moduleControllerDataAttribute),
					self = this;

				console.log('setup module: controllerClassName = %o', controllerClassName);

				if (!controllerClassName) {
					throw new Error('controller name is undefined!');
				}

				// if the module was already initialised, do nothing
				if ($module.hasClass(this.initialisedControllerCssClass)) {
					console.log('setup module: skipped because it is already initialised', controllerClassName);

					if (processedCallback) {
						processedCallback();
					}

					return;
				}

				// else, we continue the setup

				// load this module js controller class
				require(
					[
						controllerClassName
					],
					function (ControllerClass) {

						var moduleData = self.getModuleData($module);

						// instantiate the controller class
                        self.controllerInstances.add(
							new ControllerClass({
								el: $module,
								model: moduleData
							})
						);

						// Flag module as "initialized" now.
						$module.addClass(self.initialisedControllerCssClass);

						// Notify listeners that module has finished being loaded.
						$(document).trigger('moduleLoaded.yellowSubmarine', $module);

						if (processedCallback) {
							processedCallback();
						}
					}
				);
			},

			/**
			 * Initialises all web page frontend modules when the page is ready.
			 * @param {Function} [callback] Optional callback function to run when all the modules are loaded. The function signature is
			 * callback(ModuleCollection controllerInstances)
			 * @return {*}
			 */
			init: function(callback) {
				var self = this;
				$(function() {
					self.setupModules(callback);

					// Allow other frameworks to trigger module loading.
					$(document).bind('initModules.lux', function (event) {
						self.setupModules(callback);
					});
				});

				return this;
			},

            /**
            * Checks whether a specific module has already been loaded and initialised.
            * @param {String} name Name of the module which will be checked
            * @returns {Boolean} True if the module has been loaded and initialised; otherwise, returns false
            */
            isModuleLoaded: function(name) {
				return !!this.controllerInstances.findWhere({className: name});
            },

            /**
            * Gets the model associated with a specific module (if it exists).
            * @param {String} name Name of the module to retrieve the data from
            * @returns {BaseTransferModel} Model containing all the module data from Portal
            */
            getModuleModel: function(name) {
				var module = this.controllerInstances.findWhere({className: name});
				return module ? module.get('model') : null;
            },

            /**
             * Gets the event name for when a module is initialised by the Module Manager.
             * @param {String} moduleName Name of the module to listen for its initialisation
             * @returns {String} Formatted module initialisation event name
             */
            getModuleInitEventName: function(moduleName) {
                return moduleName + ':initialize';
            },

            /**
             * Triggers a custom event on the communication bus to warn interested controller(s).
             * Optional arguments may be passed to the event handler as well.
             * @param {String} name Name of the event to trigger
             */
            triggerEvent: function(name) {
				// simply pass through the optional arguments
				this.eventBus.trigger.apply(this.eventBus, arguments);
            },

            /**
             * Attach a handler to a specific event which might be triggered by other controller(s).
             * @param {BaseController} controller Controller instance which wants to listen to the event
             * @param {String} name Name of the event to listen to
             * @param {Function} callback Function which will handle the event
             */
            listenToEvent: function(controller, name, callback) {
                controller.listenTo(this.eventBus, name, callback);
            },

            /**
             * Stops listening to events triggered by other controllers.
             * @param {BaseController} controller Controller instance which wants to stops listening to events
             * @param {String} [name] Optional event name to stop listening to
             */
            stopListeningToEvents: function(controller, name) {
                controller.stopListening(this.eventBus, name);
            }
		};
	}
);

